import React, { FC } from 'react';
import produce from 'immer';
import { get, merge } from 'lodash';
import { Typography, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import FormDrawer from '~/components/FormDrawer';
import { Form, NationalitySelect } from '~/components';
import { useValidator } from '~/controllers/validation';
import { useGetApplication, useGetPayload } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getStagePayload } from '~/utils/getStagePayload';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { isReadOnly } from '~/utils/isReadOnly';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import { KsaLegalEntityShareholder } from '~/types/ksa/KsaLegalEntityShareholder';
import { getLegalEntitiesFormName } from '~/pages/ksa/application/shareholders/components/legalEntities/LegalEntityEdit';
import ReviewItem from '~/pages/ksa/review/components/ReviewItem';
import Section from '~/layouts/Section ';
import NationalityField from '~/components/fields/NationalityField';
import { Stage } from '~/types/Application';

interface Props extends Partial<FormItemInjectedProps<KsaShareholders>> {
  uid: string;
  onClose: () => void;
}

const LegalReviewEntityEdit: FC<Props> = (props) => {
  const { uid, onClose } = props;
  const { value, onChange } = props as FormItemInjectedProps<KsaShareholders>;
  const { t } = useTranslation('ksaReview');
  const reviewShareholdersFormState = { reviewShareholders: value };
  const payload = useGetPayload();

  const currentIndex =
    (payload?.shareholders as KsaShareholders)?.legalEntities?.findIndex(
      (item) => item?.uid === uid
    ) || 0;

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = produce(reviewShareholdersFormState, (draft) => {
      merge(draft, newValues);
    });
    onChange?.(updatedData.reviewShareholders);
    onClose();
  };

  return (
    <FormDrawer
      id="legalEntityReviewDrawer"
      visible={!!uid}
      onClose={onClose}
      title={t('Legal Entity shareholder')}
      formName={formName}
      withContinueButton={false}
    >
      <LegalEntityForm
        index={currentIndex}
        onFinish={onFinish}
        initialValues={reviewShareholdersFormState}
      />
    </FormDrawer>
  );
};

interface LegalEntityFormProps {
  index: number;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any | undefined;
}

const LegalEntityForm: FC<LegalEntityFormProps> = (props) => {
  const { onFinish, initialValues, index } = props;
  const { t } = useTranslation('ksaReview');

  const [form] = Form.useForm<ApplicationPayload>();
  const getFormState = () => {
    return produce(initialValues || {}, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(getFormState, undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  // const nameArr = getReviewLegalEntitiesFormName(index);

  const payload = useGetPayload();
  const legalEntity: KsaLegalEntityShareholder | undefined = get(
    payload,
    getLegalEntitiesFormName(index)
  );

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: undefined,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Form
        form={form}
        name={formName}
        onFinish={onFinish}
        onValuesChange={(_: any, newValues: any) => {
          optionalFieldsController.debouncedUpdate(newValues);
        }}
        initialValues={initialValues}
      >
        <Section>
          <Typography.Title level={4}>{t('Company license').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <ReviewItem label={t('Company legal name')}>{legalEntity?.legalName}</ReviewItem>
            {/* <Form.Item
              label={t('Company legal name')}
              name={['personalData', 'englishCompanyLegalName']}
            >
              <Input />
            </Form.Item> */}
            <ReviewItem label={t('Commercial Registration number')}>
              {legalEntity?.commercialRegistrationNumber}
            </ReviewItem>
            <ReviewItem label={t('Entity number')}>{legalEntity?.entityNumber}</ReviewItem>
            <ReviewItem label={t('Country of incorporation')}>
              <NationalityField value={legalEntity?.nationality} />
            </ReviewItem>
            {/* <Form.Item
              label={t('Country of incorporation')}
              name={['personalData', 'englishCountryOfIncorporation']}
            >
              <NationalitySelect />
            </Form.Item> */}
          </InputsGroupLayout>
        </Section>
        <Section>
          <Typography.Title level={4}>{t('Ownership share').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <ReviewItem label={t('Shares, %')}>{legalEntity?.ownershipShare?.percent}</ReviewItem>
            <ReviewItem label={t('Shares count')}>{legalEntity?.ownershipShare?.count}</ReviewItem>
          </InputsGroupLayout>
        </Section>
      </Form>
    </FormItemContextProvider>
  );
};

const formName = 'legalEntities';
// const getReviewLegalEntitiesFormName = (index: number) => ['shareholders', formName, index];

export default LegalReviewEntityEdit;
